import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import Seo from "@/components/Seo";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Politique de confidentialité"
        description="Comment Click&Gard protège vos données personnelles : finalités, durées de conservation, droits RGPD et géolocalisation approximative."
      />
      <Navbar />
      <main className="w-full px-4 md:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary items-center justify-center shadow-neon mb-5">
              <Shield className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-display tracking-tight mb-4">
              Politique de <span className="text-gradient-static">confidentialité</span>
            </h1>
            <p className="text-sm text-muted-foreground">Dernière mise à jour : 20 avril 2026</p>
          </div>

          <Card className="card-glass-premium p-8 md:p-10 rounded-3xl space-y-8 text-foreground">
            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">1. Données collectées</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Click&Gard collecte les données nécessaires au fonctionnement du service : nom, prénom,
                e-mail, téléphone, adresse postale, photo de profil, pièces d'identité (pour les professionnels),
                ainsi que les données liées aux réservations et messages échangés sur la plateforme.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Données techniques de connexion (professionnels uniquement)</strong> :
                à des fins de sécurité et de prévention de la fraude, nous enregistrons à chaque inscription et
                à chaque connexion d'un compte professionnel : l'adresse IP, la localisation approximative déduite
                de cette IP (ville, région, pays, fournisseur d'accès, coordonnées GPS approximatives) et le
                user-agent (type de navigateur, système d'exploitation, appareil utilisé). Aucune géolocalisation
                précise via le GPS de l'appareil n'est collectée.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">2. Finalités du traitement</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground leading-relaxed">
                <li>Création et gestion des comptes utilisateurs</li>
                <li>Mise en relation parents / professionnels</li>
                <li>Vérification d'identité des professionnels</li>
                <li>Traitement des paiements et abonnements</li>
                <li>Notifications transactionnelles par e-mail</li>
                <li>Sécurité, traçabilité des connexions professionnelles et prévention de la fraude
                  (détection de comptes frauduleux, multi-comptes, usurpation d'identité)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">3. Base légale</h2>
              <p className="text-muted-foreground leading-relaxed">
                Le traitement repose sur l'exécution du contrat (CGU), votre consentement
                (notamment pour les données sensibles comme l'identité), et notre intérêt légitime
                à sécuriser la plateforme.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">4. Conservation des données</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Vos données sont conservées tant que votre compte est actif. À la suppression du compte,
                elles sont effacées sous 30 jours, sauf obligations légales de conservation
                (facturation, lutte contre la fraude).
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Les <strong className="text-foreground">données techniques de connexion</strong> des professionnels
                (IP, géolocalisation approximative, user-agent) sont conservées pendant une durée maximale de
                <strong className="text-foreground"> 12 mois</strong>, puis automatiquement purgées.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">5. Vos droits (RGPD)</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Conformément au Règlement Général sur la Protection des Données, vous disposez d'un droit
                d'accès, de rectification, d'effacement, de portabilité, d'opposition et de limitation
                du traitement de vos données.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Pour exercer ces droits, contactez-nous à{" "}
                <a href="mailto:contact@clickandgard.fr" className="text-primary hover:underline">
                  contact@clickandgard.fr
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">6. Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                Click&Gard utilise uniquement des cookies techniques essentiels au fonctionnement du site
                (session d'authentification). Aucun cookie publicitaire ou de suivi tiers n'est déposé sans votre consentement.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">7. Sécurité</h2>
              <p className="text-muted-foreground leading-relaxed">
                Nous mettons en œuvre des mesures techniques et organisationnelles (chiffrement,
                authentification, accès restreint, RLS) pour protéger vos données contre tout accès non autorisé.
                Les données techniques de connexion des professionnels ne sont accessibles qu'aux administrateurs
                habilités de Click&Gard, dans le cadre exclusif de la sécurité de la plateforme.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3 text-primary">8. Destinataires des données</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vos données ne sont jamais vendues. Elles sont accessibles à notre équipe technique et
                administrative, ainsi qu'à nos sous-traitants techniques (hébergement cloud, traitement
                des paiements via Stripe, envoi d'e-mails via Resend, géolocalisation IP via ip-api.com).
                Tous nos sous-traitants sont engagés contractuellement à respecter le RGPD.
              </p>
            </section>
          </Card>

          <div className="mt-8 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
